import { Check, ChevronRight, MessageSquareWarning } from "lucide-react";
import { createContext, useContext, type ReactNode, useState } from "react";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import {
  getSectionItemReferences,
  type StatementItemReference,
} from "./statement-item-reference";
import {
  BLOCK_TYPE,
  type DividerBlock,
  type KeyValueBlock,
  type LineItem,
  type Metric,
  type NoteBlock,
  type PayStatement,
  type SectionBlock,
  type StatementBlock,
  type SummaryBlock,
  type TotalsBlock,
} from "./statement-schema";

export type { StatementItemReference } from "./statement-item-reference";

// Renders a PayStatement as one flat, scannable surface — no nested cards. The
// statement is a single sheet: a header, the at-a-glance breakdown, then the
// line items as hairline-divided rows. Everything sits in one centered column
// so it uses the full width on a phone and stays readable on a wide screen.

/**
 * Lets a consumer put a "question this" action on every figure in the statement.
 *
 * Passed through context rather than down the block tree: blocks nest, and only
 * the technician's own view supplies this — an admin preview renders the same
 * statement with no actions at all.
 *
 * Because the affordance hangs off the rendered items, the extractor already
 * decides what is questionable by deciding what exists. If we later want finer
 * control, a flag on the schema can gate it without changing this shape.
 */
/** Whether the question already asked about a figure is still open. */
export const STATEMENT_ITEM_MARK = {
  open: "open",
  closed: "closed",
} as const;

export type StatementItemMark =
  (typeof STATEMENT_ITEM_MARK)[keyof typeof STATEMENT_ITEM_MARK];

export const STATEMENT_ITEM_ACTION_INTENT = {
  askQuestion: "ask-question",
  startThread: "start-thread",
} as const;

export type StatementItemActionIntent =
  (typeof STATEMENT_ITEM_ACTION_INTENT)[keyof typeof STATEMENT_ITEM_ACTION_INTENT];

export interface StatementItemAction {
  onAction: (reference: StatementItemReference) => void;
  /** Changes the unmarked figure action from asking a question to starting a thread. */
  intent?: StatementItemActionIntent;
  /** `referenceKey` of the figure currently attached, so it can be marked. */
  activeKey?: string | null;
  /**
   * Figures that already carry a thread, keyed by `statementRowKey`.
   *
   * A marked row shows the thread's state instead of the ask affordance, and its
   * action takes the reader to that conversation rather than starting a second
   * one about the same number.
   */
  marks?: Record<string, StatementItemMark>;
  /**
   * The figure the reader currently has a conversation open about, by
   * `statementRowKey`. Its row is tinted so the pack says which number is being
   * discussed, rather than leaving a reader to match a label by eye.
   *
   * Keyed like `marks` rather than like `activeKey`: a rerun that changes an amount
   * must not lose the row, which is the same reason the marks drop the amount.
   */
  activeRowKey?: string | null;
  /**
   * Only marked figures get a control at all.
   *
   * For a reader who is not the one asking questions: on the payroll side an ask
   * affordance beside every figure is a row of identical icons competing with the
   * two marks that actually carry state, and the marks are the signal.
   */
  marksOnly?: boolean;
}

const ItemActionContext = createContext<StatementItemAction | null>(null);

/**
 * The mark on the figure a conversation is about.
 *
 * Behind the type rather than behind the cell: a block covering a label, its value
 * and the gap between them is a lot of furniture for "this one", and it squares off
 * a layout that is otherwise all text. This sits behind the glyphs like a
 * highlighter, so the figure is picked out and the pack still reads as a pack.
 *
 * Inline, with `box-decoration-clone` so a value that wraps keeps its rounded ends
 * on every line, and negative margin matching the padding so nothing moves when it
 * appears — clicking between one technician's questions must not reflow their pack.
 */
const ACTIVE_TEXT = "-mx-1 box-decoration-clone rounded bg-gold-5 px-1";

/**
 * Every row a section covers: its line items, and the detail fields under each.
 *
 * So a group that is closed can still say the figure under discussion is inside it
 * — otherwise the tint is hidden behind the very thing hiding the row.
 */
const sectionRowKeys = (block: SectionBlock) =>
  getSectionItemReferences(block).map(statementRowKey);

/** Whether this figure is the one the open conversation is about. */
const useActiveRow = (reference: StatementItemReference) => {
  const action = useContext(ItemActionContext);
  const active = action?.activeRowKey;
  return Boolean(active) && active === statementRowKey(reference);
};

export const referenceKey = (reference: StatementItemReference): string =>
  [reference.heading ?? "", reference.line ?? "", reference.amount ?? ""].join(
    "|",
  );

/**
 * The identity of a row across reruns — everything `referenceKey` has except the
 * amount.
 *
 * `referenceKey` is right for marking the figure attached in this render, where
 * the amount is part of what was picked. It is wrong for matching a thread to the
 * row it is about: the number is exactly what changes when payroll fixes the
 * thing being disputed, and the thread has to stay attached across that rerun.
 *
 * Labels are a weaker identity than an anchor's accessor key, which is what the
 * extractor follows to build these rows. Until a stable id rides along in the
 * statement schema, this is the best available: measured against every thread in
 * the seeded Dean's period, all eleven matched exactly one row.
 */
export const statementRowKey = (reference: StatementItemReference): string =>
  [reference.heading, reference.line]
    .map((part) => (part ?? "").toLowerCase().replace(/\s+/g, " ").trim())
    .join("|");

/**
 * The per-figure action. Renders nothing when no consumer supplied one, and
 * nothing for a figure with neither a label nor a heading — there would be no way
 * to say afterwards what was being questioned.
 */
const StatementItemActionButton = (reference: StatementItemReference) => {
  const action = useContext(ItemActionContext);
  if (!action) return null;
  if (!reference.line && !reference.heading) return null;

  const key = referenceKey(reference);
  const active = action.activeKey === key;
  const mark = action.marks?.[statementRowKey(reference)];
  if (action.marksOnly && !mark) return null;
  // `||`, not `??`, to match the guard above: a present-but-empty `line` is not
  // something to announce, and `??` kept it — leaving the button labelled
  // "Question " with nothing after it.
  const name = reference.line || reference.heading || "this figure";
  const startsThread =
    action.intent === STATEMENT_ITEM_ACTION_INTENT.startThread;
  const label = mark
    ? `${mark === STATEMENT_ITEM_MARK.open ? "Open" : "Answered"} question about ${name}`
    : startsThread
      ? `Start a thread about ${name}`
      : `Question ${name}`;
  // A 7px dot says something is here and nothing about what, so the tooltip says
  // both the state and what the click does. Those differ by consumer and the mark
  // is what tells them apart: a marked figure has a conversation to open, an
  // unmarked one is where a new one starts.
  const hint = mark
    ? `${mark === STATEMENT_ITEM_MARK.open ? "Open question" : "Answered question"} about ${name}. Click to read it.`
    : active
      ? startsThread
        ? `This thread will be about ${name}.`
        : `This thread is about ${name}.`
      : startsThread
        ? `Start a thread about ${name}.`
        : `Ask about ${name}.`;

  return (
    <Tooltip>
      {/* asChild, not asDiv: this mark sits inside the value's own <p>, and a
          wrapper div there is invalid nesting — React says so and the browser
          silently closes the paragraph early. The button is never disabled, which
          is the case asDiv exists for. */}
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => action.onAction(reference)}
          aria-label={label}
          aria-pressed={"activeKey" in action ? active : undefined}
          className={cn(
            // ml-1 + align-middle rather than a gap on the parent: in the summary
            // grid this sits inline inside the label so it follows the last word of
            // a label that wraps, instead of stranding out beside the block.
            "ml-1 shrink-0 rounded p-1 align-middle text-tertiary-text transition-colors",
            "hover:bg-surface-secondary hover:text-primary-text",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1",
            { "bg-surface-secondary text-primary-text": active },
          )}
        >
          {/* A marked row says what state its conversation is in, in the same two
              marks PackChat uses everywhere else: an amber dot while it is open, a
              green check once it is answered. */}
          {mark === STATEMENT_ITEM_MARK.open ? (
            <span
              className="block size-[7px] rounded-full bg-gold-60"
              aria-hidden
            />
          ) : mark === STATEMENT_ITEM_MARK.closed ? (
            <Check className="size-3.5 text-green-60" aria-hidden />
          ) : (
            <MessageSquareWarning className="h-4 w-4" aria-hidden />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent maxWidth="sm">{hint}</TooltipContent>
    </Tooltip>
  );
};

interface StatementRendererProps {
  statement: PayStatement;
  footer?: ReactNode;
  /** Supply to put a "question this figure" action on every row. */
  itemAction?: StatementItemAction;
  /**
   * Whose pack this is, as its own titled block.
   *
   * Off where the surface around it already says: beside a conversation with the
   * technician, repeating their name is a second copy of the same fact and its
   * rule lands a few pixels off the neighbouring pane's.
   */
  showTitle?: boolean;
}

const COLUMN = "mx-auto w-full max-w-2xl px-4";
const GRID = "grid grid-cols-2 gap-x-4 gap-y-3";
const WIDE_VALUE_LENGTH = 28;

const MetricLabel = ({ children }: { children?: string | null }) =>
  children ? (
    <p className="text-[11px] font-medium uppercase tracking-wide text-tertiary-text">
      {children}
    </p>
  ) : null;

/**
 * A value with its mark held on the same line as the last word of it.
 *
 * The mark used to be a plain sibling of the text, which is fine until the value
 * wraps: in the PackChat rail's two-column grid "$1,700.83 · 7 installs" breaks,
 * and the dot went with the overflow onto a line of its own, sitting under the
 * amount attached to nothing. Only the final word has to stay unbreakable, so
 * that is the only part wrapped.
 */
const ValueWithMark = ({
  value,
  active,
  children,
}: {
  value: string;
  /** Whether this is the figure the open conversation is about. */
  active?: boolean;
  children: ReactNode;
}) => {
  const lastSpace = value.lastIndexOf(" ");
  return (
    <span className={cn({ [ACTIVE_TEXT]: active })}>
      {lastSpace === -1 ? null : value.slice(0, lastSpace + 1)}
      <span className="whitespace-nowrap">
        {lastSpace === -1 ? value : value.slice(lastSpace + 1)}
        {children}
      </span>
    </span>
  );
};

// One label-over-value cell, shared by the summary breakdown and the line-item
// details so they read the same. Long values span the full row.
const Field = ({
  metric,
  strong,
  prominent,
  muted,
  heading,
}: {
  metric: Metric;
  strong?: boolean;
  prominent?: boolean;
  muted?: boolean;
  heading?: string;
}) => {
  const active = useActiveRow({
    heading,
    line: metric.label ?? undefined,
    amount: metric.value,
  });

  return (
    <Stack
      gap="none"
      className={cn("min-w-0 gap-0.5", {
        "col-span-2": metric.value.length > WIDE_VALUE_LENGTH,
      })}
    >
      <MetricLabel>{metric.label}</MetricLabel>
      {/* The action sits inline after the value, not the label: these labels are
          long enough to wrap in a two-column grid on a phone, and values are not.
          It also matches the line-item and key/value rows, where the action always
          follows the amount. */}
      {prominent ? (
        <Typography
          variant="subtitleW3"
          className={cn("break-words", { "text-tertiary-text": muted })}
        >
          <ValueWithMark value={metric.value} active={active}>
            <StatementItemActionButton
              heading={heading}
              line={metric.label ?? undefined}
              amount={metric.value}
            />
          </ValueWithMark>
        </Typography>
      ) : (
        <p
          className={cn("break-words text-[13px] text-secondary-text", {
            "font-semibold text-primary-text": strong,
          })}
        >
          <ValueWithMark value={metric.value} active={active}>
            <StatementItemActionButton
              heading={heading}
              line={metric.label ?? undefined}
              amount={metric.value}
            />
          </ValueWithMark>
        </p>
      )}
    </Stack>
  );
};

// A small pill marking the pay source that was actually paid. Only the paid
// source is badged; the unpaid one reads as such from its greyed card alone.
const PaidBadge = () => (
  <span className="shrink-0 rounded-full bg-green-10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-700">
    Paid
  </span>
);

// The at-a-glance breakdown: the headline pay figure + supporting metrics.
// When `variant` is set the card becomes a pay-source tile — the paid source is
// highlighted, the unpaid one is greyed — mirroring the earnings email.
const Summary = ({ block }: { block: SummaryBlock }) => {
  const paid = block.variant === "paid";
  const unpaid = block.variant === "unpaid";
  // Only these two are pay-source tiles; anything else falls back to the plain
  // card (the endpoint type-casts rather than runtime-validates the payload).
  const isTile = paid || unpaid;

  const hero = block.hero && (
    <Stack gap="none" className="min-w-0 gap-1">
      <p className="text-sm font-semibold uppercase tracking-wide text-tertiary-text">
        {block.hero.label}
      </p>
      <Typography
        variant="titleW2"
        className={cn("leading-none", { "text-tertiary-text": unpaid })}
      >
        {block.hero.value}
      </Typography>
    </Stack>
  );

  const body = (
    <Stack fullWidth gap="none" className="gap-4">
      {/* Plain cards render the hero unwrapped (DOM-identical to before). Tiles
          put the hero + Paid badge in a top row; the badge is independent of the
          optional hero, so a metrics-only paid tile still shows it. */}
      {isTile
        ? (block.hero || paid) && (
            <Stack
              direction="row"
              align="start"
              justify={block.hero ? "between" : "end"}
              fullWidth
              gap="md"
            >
              {hero}
              {paid && <PaidBadge />}
            </Stack>
          )
        : hero}
      {block.metrics && block.metrics.length > 0 && (
        <div className={GRID}>
          {block.metrics.map((metric, index) => (
            <Field key={index} metric={metric} prominent muted={unpaid} />
          ))}
        </div>
      )}
    </Stack>
  );

  if (!isTile) return body;

  // Pay-source tile: green-tinted when paid, muted grey when not.
  return (
    <Stack
      fullWidth
      gap="none"
      className={cn("rounded-xl border p-4", {
        "border-green-40 bg-green-5": paid,
        "border-stroke-tertiary bg-surface-secondary/50": unpaid,
      })}
    >
      {body}
    </Stack>
  );
};

const LineItemRow = ({
  item,
  heading,
}: {
  item: LineItem;
  heading?: string;
}) => {
  const active = useActiveRow({
    heading,
    line: item.title,
    amount: item.amount,
  });

  return (
    <Stack fullWidth gap="none" className="gap-2 py-3.5">
      <Stack direction="row" align="start" justify="between" fullWidth gap="md">
        <Stack gap="none" className="min-w-0 gap-0.5">
          {item.eyebrow && (
            <p className="text-sm text-tertiary-text">{item.eyebrow}</p>
          )}
          <Typography
            variant="subtitleW3"
            className="min-w-0 break-words leading-snug"
          >
            <span className={cn({ [ACTIVE_TEXT]: active })}>{item.title}</span>
          </Typography>
        </Stack>
        <Stack direction="row" align="center" gap="none" className="shrink-0">
          {item.amount && (
            <Typography
              variant="subtitleW3"
              className="shrink-0 text-right leading-snug"
            >
              {item.amount}
            </Typography>
          )}
          <StatementItemActionButton
            heading={heading}
            line={item.title}
            amount={item.amount}
          />
        </Stack>
      </Stack>

      {item.fields && item.fields.length > 0 && (
        <div className={GRID}>
          {item.fields.map((field, index) => (
            <Field
              key={index}
              metric={field}
              strong={field.strong}
              // The job, not the section — "Commission · $120" on its own doesn't
              // say which job a technician is asking about.
              heading={item.title}
            />
          ))}
        </div>
      )}

      {item.note && (
        <p className="text-[13px] text-secondary-text">{item.note}</p>
      )}
    </Stack>
  );
};

const SectionHeading = ({ left, right }: { left: string; right?: string }) => (
  <Stack direction="row" align="baseline" justify="between" fullWidth gap="md">
    <Typography variant="subtitleW2" className="min-w-0 truncate">
      {left}
    </Typography>
    {right && (
      <Typography variant="captionW1" textColor="tertiary" className="shrink-0">
        {right}
      </Typography>
    )}
  </Stack>
);

// A grouped section (one with a subtotal — e.g. a per-invoice job) is
// collapsible and starts collapsed, so the statement reads as a scannable list
// of jobs + subtotals; the technician taps one to see its line items. Plain
// (un-grouped) sections render expanded, exactly as before.
const Section = ({ block }: { block: SectionBlock }) => {
  const collapsible = Boolean(block.total);
  const [open, setOpen] = useState(!collapsible);
  // A closed group hides the row the conversation is about, so the group carries
  // the tint in its place. Open, the row wears it itself and the header does not.
  const action = useContext(ItemActionContext);
  const activeKey = action?.activeRowKey;
  const holdsActiveRow =
    Boolean(activeKey) && sectionRowKeys(block).includes(activeKey ?? "");
  const right = block.total
    ? block.total.value
    : `${block.items.length} ${block.items.length === 1 ? "entry" : "entries"}`;

  const items = open && (
    // Indent the content to align under the heading (past the chevron gutter).
    // w-full so short-content groups still span the column (the parent Stack is
    // items-start, so without it a group of small values shrinks to its content).
    <div
      className={cn("w-full divide-y divide-stroke-tertiary", {
        "pl-5": collapsible,
      })}
    >
      {block.items.map((item, index) => (
        <LineItemRow
          key={`${item.title}-${index}`}
          item={item}
          heading={block.heading}
        />
      ))}
    </div>
  );

  if (!collapsible) {
    return (
      <Stack fullWidth gap="none" className="gap-1">
        {block.heading && <SectionHeading left={block.heading} right={right} />}
        {items}
      </Stack>
    );
  }

  return (
    <Stack fullWidth gap="none" className="gap-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-2 text-left"
      >
        <div className="min-w-0">
          {/* chevron on the same line as the name → vertically centered with it;
              caption + content are indented (pl-5) to align under the name */}
          <div className="flex min-w-0 items-center gap-2">
            <ChevronRight
              className={cn(
                "h-3 w-3 shrink-0 text-tertiary-text transition-transform",
                { "rotate-90": open },
              )}
            />
            <Typography variant="subtitleW2" className="min-w-0 truncate">
              <span className={cn({ [ACTIVE_TEXT]: !open && holdsActiveRow })}>
                {block.heading ?? ""}
              </span>
            </Typography>
          </div>
          {block.caption && (
            <p className="truncate pl-5 text-sm text-tertiary-text">
              {block.caption}
            </p>
          )}
        </div>
        <Typography variant="subtitleW2" className="shrink-0">
          {right}
        </Typography>
      </button>
      {items}
    </Stack>
  );
};

// Its own component so the row can ask whether it is the one being discussed;
// a hook cannot live inside the map.
const KeyValueRow = ({
  row,
  heading,
}: {
  row: KeyValueBlock["rows"][number];
  heading?: string;
}) => {
  const active = useActiveRow({
    heading,
    line: row.label ?? undefined,
    amount: row.value,
  });

  return (
    <Stack
      direction="row"
      align="baseline"
      justify="between"
      fullWidth
      gap="md"
    >
      <Typography variant="bodyW3" textColor="secondary">
        {row.label}
      </Typography>
      <Stack direction="row" align="center" gap="none" className="shrink-0">
        <Typography
          variant="bodyW3"
          className={cn("shrink-0 text-right", {
            "font-semibold": row.strong,
          })}
        >
          <span className={cn({ [ACTIVE_TEXT]: active })}>{row.value}</span>
        </Typography>
        <StatementItemActionButton
          heading={heading}
          line={row.label ?? undefined}
          amount={row.value}
        />
      </Stack>
    </Stack>
  );
};

const KeyValue = ({ block }: { block: KeyValueBlock }) => (
  <Stack fullWidth gap="none" className="gap-3">
    {block.heading && <SectionHeading left={block.heading} />}
    <Stack fullWidth gap="none" className="gap-2.5">
      {block.rows.map((row, index) => (
        <KeyValueRow
          key={index}
          row={row}
          heading={block.heading ?? undefined}
        />
      ))}
    </Stack>
  </Stack>
);

// Extracted for the same reason as KeyValueRow: the row itself has to ask whether
// it is the figure under discussion, and a hook cannot live inside the map.
const TotalsItem = ({
  item,
  heading,
}: {
  item: TotalsBlock["items"][number];
  heading?: string;
}) => {
  const active = useActiveRow({
    heading,
    line: item.label ?? undefined,
    amount: item.value,
  });

  return (
    <Stack gap="none" align="end" className="gap-0.5">
      <Stack direction="row" align="center" gap="none">
        <MetricLabel>{item.label}</MetricLabel>
        <StatementItemActionButton
          heading={heading}
          line={item.label ?? undefined}
          amount={item.value}
        />
      </Stack>
      <Typography variant="subtitleW3">
        <span className={cn({ [ACTIVE_TEXT]: active })}>{item.value}</span>
      </Typography>
    </Stack>
  );
};

const Totals = ({ block }: { block: TotalsBlock }) => (
  <Stack direction="row" align="center" justify="between" fullWidth gap="md">
    <Typography
      variant="subtitleW3"
      className="uppercase tracking-wide"
      textColor="secondary"
    >
      {block.label}
    </Typography>
    <Stack direction="row" gap="lg" align="end" className="flex-wrap">
      {block.items.map((item, index) => (
        <TotalsItem
          key={index}
          item={item}
          heading={block.label ?? undefined}
        />
      ))}
    </Stack>
  </Stack>
);

const Note = ({ block }: { block: NoteBlock }) => (
  <p className="text-[13px] text-secondary-text">{block.text}</p>
);

// Labelled separator that introduces the run of blocks beneath it (e.g. one per
// source sheet for a multi-sheet technician). Reads as a quiet group heading; the
// surrounding hairline dividers provide the visual break.
const Divider = ({ block }: { block: DividerBlock }) => (
  <Typography
    variant="captionW1"
    className="font-semibold uppercase tracking-wide text-tertiary-text"
  >
    {block.label}
  </Typography>
);

const renderBlock = (block: StatementBlock, index: number) => {
  switch (block.type) {
    case BLOCK_TYPE.summary:
      return <Summary key={index} block={block} />;
    case BLOCK_TYPE.section:
      return <Section key={index} block={block} />;
    case BLOCK_TYPE.keyValue:
      return <KeyValue key={index} block={block} />;
    case BLOCK_TYPE.totals:
      return <Totals key={index} block={block} />;
    case BLOCK_TYPE.note:
      return <Note key={index} block={block} />;
    case BLOCK_TYPE.divider:
      return <Divider key={index} block={block} />;
    default:
      // A block type this build doesn't recognize (e.g. the API shipped a new one
      // before the FE caught up). Surface a muted placeholder rather than silently
      // dropping it — silent pay-data loss is worse than a visible gap.
      return (
        <p key={index} className="text-[13px] italic text-tertiary-text">
          This item can’t be displayed. Please update the app.
        </p>
      );
  }
};

// Key blocks by content + position: the content prefix keeps a Section's local
// open/closed state with its block when the viewer switches statements, while
// the trailing index disambiguates the common case of repeated content (e.g.
// several per-invoice job sections that share the technician's name as heading),
// which a content-only key would collide on.
const blockKey = (block: StatementBlock, index: number): string => {
  switch (block.type) {
    case BLOCK_TYPE.summary:
      return `summary:${block.hero?.label ?? ""}:${index}`;
    case BLOCK_TYPE.section:
      return `section:${block.heading ?? ""}:${index}`;
    case BLOCK_TYPE.keyValue:
      return `kv:${block.heading ?? ""}:${index}`;
    case BLOCK_TYPE.totals:
      return `totals:${block.label ?? ""}:${index}`;
    case BLOCK_TYPE.divider:
      return `divider:${block.label ?? ""}:${index}`;
    default:
      return `${block.type}:${index}`;
  }
};

// The whole statement as one flat list of hairline-divided rows in the centered
// column — every layout (flat, stacked, merged) is just a different block list.
const BlockList = ({ blocks }: { blocks: StatementBlock[] }) => (
  <div className={cn(COLUMN, "divide-y divide-stroke-tertiary")}>
    {blocks.map((block, index) => (
      <div key={blockKey(block, index)} className="py-5 first:pt-4 last:pb-8">
        {renderBlock(block, index)}
      </div>
    ))}
  </div>
);

export const StatementRenderer = ({
  statement,
  footer,
  itemAction,
  showTitle = true,
}: StatementRendererProps) => (
  <ItemActionContext.Provider value={itemAction ?? null}>
    {/* The whole statement scrolls as one — the title scrolls away with the content
      rather than staying pinned. */}
    {/* `pb-6` inside the scroll: at the end of a long pack the last row would
        otherwise sit flush against whatever frames this. */}
    <Stack
      gap="none"
      align="stretch"
      fullWidth
      className="quiet-scrollbar h-full min-h-0 overflow-y-auto bg-surface-primary pb-6"
    >
      {showTitle && (
        <Stack gap="none" align="stretch" fullWidth>
          {/* The rule sits on the column, not on the page: full-bleed it ran the
            whole width of a desktop viewport while the pack it divides is a
            centred column, which reads as a stripe across empty space. */}
          <Stack
            gap="none"
            className={cn(
              COLUMN,
              "gap-0.5 border-b border-stroke-tertiary py-4",
            )}
          >
            <Typography
              variant="subtitleW1"
              className="min-w-0 truncate text-primary-text"
            >
              {statement.title}
            </Typography>
            {statement.subtitle && (
              <Typography variant="captionW1" textColor="tertiary">
                {statement.subtitle}
              </Typography>
            )}
          </Stack>
        </Stack>
      )}

      {statement.blocks.length > 0 ? (
        <BlockList blocks={statement.blocks} />
      ) : (
        <div className={cn(COLUMN, "py-10")}>
          <Typography variant="bodyW3" textColor="tertiary">
            No pay to show for this period.
          </Typography>
        </div>
      )}

      {footer && (
        <Stack fullWidth className={cn(COLUMN, "pb-10")}>
          {footer}
        </Stack>
      )}
    </Stack>
  </ItemActionContext.Provider>
);
